/**
 * The project-wide stores a pinned frame reads beside its own data (plan 12
 * §8 Data). A pin window loads them for its project with the Workspace's own
 * loaders, and the owner relays the socket events that change them
 * (`desktop-pin-relay.ts`); the window applies those through the stores'
 * own handlers. Task events reach the owner through the user's group for
 * every project; memory changes arrive as an update of the project's memory
 * frame, which the owner hears for the current and the pinned projects.
 */
import type { FrameType } from "@/api/frame-model";
import type { WsMessage } from "@/api/websocket";
import { applyTaskStoreEvent } from "@/lib/companion-frame-reducer-task-runtime";
import { useMemoryStore } from "@/stores/memory-store";
import { useTasksStore } from "@/stores/tasks-store";

/** owner → widgets: a socket event that changes a pinned project's stores. */
export const DESKTOP_PIN_PROJECT_EVENT = "denker://desktop-pin-project-event";

export interface DesktopPinProjectEventPayload {
  /** The event's project; null when it names none (task id only). */
  projectId: string | null;
  message: WsMessage;
}

export type PinProjectStore = "tasks" | "memory";

const TASKS: readonly PinProjectStore[] = ["tasks"];
const MEMORY: readonly PinProjectStore[] = ["memory"];
const NONE: readonly PinProjectStore[] = [];

/** The frame types whose components or faces read a project-wide store. */
const STORES_BY_FRAME_TYPE: Partial<
  Record<FrameType, readonly PinProjectStore[]>
> = {
  taskboard: TASKS,
  github: TASKS,
  // Comment anchors are the frame's tasks.
  doc: TASKS,
  markdown: TASKS,
  memory: MEMORY,
};

/** The stores a pinned frame of this type reads; the same array per type. */
export function pinProjectStores(
  frameType: FrameType | undefined,
): readonly PinProjectStore[] {
  return (frameType && STORES_BY_FRAME_TYPE[frameType]) || NONE;
}

const TASK_EVENTS: ReadonlySet<string> = new Set([
  "task.created",
  "task.updated",
  "task.status_changed",
  "task.blocked",
  "task.unblocked",
]);

function isMemoryFrameEvent(msg: WsMessage): boolean {
  return (
    (msg.type === "frame.created" || msg.type === "frame.updated") &&
    msg.data.frame_type === "memory"
  );
}

/**
 * The store a socket event changes, when the owner relays it to the widgets
 * that read that store; null for every other event.
 */
export function pinProjectEventStore(msg: WsMessage): PinProjectStore | null {
  if (TASK_EVENTS.has(msg.type)) return "tasks";
  return isMemoryFrameEvent(msg) ? "memory" : null;
}

/**
 * Load the stores for the window's project. The memory loaders read the
 * project from the project store, which holds the pin's project here.
 */
export function loadPinProjectStores(stores: readonly PinProjectStore[]): void {
  if (stores.includes("tasks")) void useTasksStore.getState().fetchTasks();
  if (stores.includes("memory")) void useMemoryStore.getState().refresh();
}

/**
 * Apply a relayed event to the stores this window loaded for `projectId`.
 * An event of another project is ignored; one naming no project changes
 * only the tasks this window holds (its handlers match by task id).
 */
export function applyPinProjectEvent(
  stores: readonly PinProjectStore[],
  projectId: string,
  { projectId: eventProject, message }: DesktopPinProjectEventPayload,
): void {
  if (eventProject !== null && eventProject !== projectId) return;
  if (stores.includes("tasks") && TASK_EVENTS.has(message.type))
    applyTaskStoreEvent(message, useTasksStore);
  if (stores.includes("memory") && isMemoryFrameEvent(message))
    void useMemoryStore.getState().refresh();
}
